import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Client, ResponseType } from '@microsoft/microsoft-graph-client';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { AzureAuthService } from '../auth';

/**
 * Service to interact with Microsoft Graph API for user-related operations.
 */
@Injectable({
  providedIn: 'root',
})
export class UserAzureGraphService {
  private readonly authService = inject(AzureAuthService);
  private readonly snack = inject(MatSnackBar);

  public graphClient: Client = this.initClient();

  /**
   * Fetches a single user from Microsoft Graph API by their email address.
   * @param email The email address of the user to fetch.
   * @returns A promise resolving to the user object or undefined if not found.
   */
  public async getSingleUserByEmail(email: string) {
    if (!this.authService.authService.instance && !this.graphClient) {
      return undefined;
    }

    try {
      // Get the user from Graph (GET /me)
      const graphUserResult: { value: any[] } = await this.graphClient?.api('/users').query(`?$filter=mail eq '${email}'`).select('displayName,photo').get();

      return graphUserResult.value[0];
    } catch (error) {
      console.error('Error getting user by email', error);
      this.snack.open('Error getting user', 'Dismiss');
      return undefined;
    }
  }

  /**
   * Fetches a single user from Microsoft Graph API by their user ID.
   * @param id The ID of the user to fetch.
   * @returns A promise resolving to the user object or undefined if not found.
   */
  public async getSingleUserById(id: string) {
    if (!this.authService.authService.instance && !this.graphClient) {
      return undefined;
    }

    try {
      // Get the user from Graph (GET /me)
      const graphUserResult: { value: any[] } = await this.graphClient
        ?.api('/users')
        .query(`?$filter=id eq '${id}'`)
        .select('displayName,mail,userPrincipalName,photo')
        .get();

      return graphUserResult.value[0];
    } catch (error) {
      console.error('Error getting user by ID', error);
      this.snack.open('Error getting user', 'Dismiss');
      return undefined;
    }
  }

  /**
   * Fetches the profile photo of a user by their Azure Object ID.
   * Returns a data URL string, or undefined if no photo is available.
   * @param id The Azure Object ID of the user.
   */
  public async getUserPhotoById(id: string): Promise<string | undefined> {
    if (!this.authService.authService.instance && !this.graphClient) {
      return undefined;
    }

    try {
      const buffer: ArrayBuffer = await this.graphClient.api(`/users/${id}/photo/$value`).responseType(ResponseType.ARRAYBUFFER).get();
      const blob = new Blob([buffer], { type: 'image/jpeg' });
      return await firstValueFrom(AzureAuthService.createImageFromBlob(blob));
    } catch {
      return undefined;
    }
  }

  /**
   * Initializes the Microsoft Graph client with an authentication provider.
   * @returns An instance of the Microsoft Graph client.
   */
  private initClient() {
    return Client.init({
      // Initialize the Graph client with an auth
      // provider that requests the token from the
      // auth service
      authProvider: (done) => {
        this.authService.authService.instance
          .acquireTokenSilent({
            scopes: ['user.read'],
          })
          .then((response) => {
            done(null, response.accessToken);
          })
          .catch((reason) => {
            console.error('Error acquiring token', reason);
            done(reason, null);
          });
      },
    });
  }
}
